package com.siriusxm.unifiedcontent.console.player

import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*

/** Shared presentation for the persistent transport and its expanded now-playing panel. */
object AudioPlayerStyles : StyleSheet() {

    val playerArea by style {
        margin(0.px)
        flexShrink(0)
        borderRadius(14.px)
        backgroundColor(SXMUI.playerDrawerBackgroundColor.value())
        color(SXMUI.playerForegroundColor.value())
        property("box-shadow", "0 2px 12px ${SXMUI.boxShadowColor.value()}")
        overflow("hidden")
    }

    val primaryRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(22.px)
        padding(14.px, 22.px)
        backgroundColor(SXMUI.playerBackgroundColor.value())
        minHeight(88.px)
        property("box-sizing", "border-box")
    }

    val sourceIdentity by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(18.px)
        flex("0 1 290px")
        minWidth(180.px)
        paddingRight(22.px)
        property("border-right", "1px solid ${SXMUI.playerDividerColor.value()}")
    }

    val sourceWithDetails by style {
        flexBasis(420.px)
    }

    val previewImage by style {
        width(60.px)
        height(60.px)
        flexShrink(0)
        borderRadius(8.px)
        overflow("hidden")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(SXMUI.playerTrackColor.value())
    }

    val titleArea by style {
        minWidth(0.px)
        flex(1)
    }

    val title by style {
        fontSize(1.3.cssRem)
        fontWeight(700)
        whiteSpace("nowrap")
    }

    val sourceCaption by style {
        marginTop(6.px)
        fontSize(.72.cssRem)
        fontWeight(600)
        property("letter-spacing", ".13em")
        property("text-transform", "uppercase")
        color(SXMUI.playerMutedColor.value())
    }

    val compactNowPlaying by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
        marginTop(5.px)
        minWidth(0.px)
    }

    val nowPlayingLabel by style {
        color(SXMUI.playerAccentColor.value())
        fontSize(.65.cssRem)
        fontWeight(700)
        whiteSpace("nowrap")
        property("letter-spacing", ".04em")
    }

    val cutSummary by style {
        fontSize(.85.cssRem)
        minWidth(0.px)
    }

    val transport by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(20.px)
        flexShrink(0)
    }

    val playButton by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        flexShrink(0)
        width(56.px)
        height(56.px)
        borderRadius(50.percent)
        border(0.px)
        backgroundColor(SXMUI.playerControlSurface.value())
        color(SXMUI.playerBackgroundColor.value())
        cursor("pointer")
        self + disabled style {
            opacity(.3)
            cursor("default")
        }
        self + focusVisible style {
            property("outline", "2px solid ${SXMUI.playerAccentColor.value()}")
            property("outline-offset", "4px")
        }
    }

    val iconButton by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        flexShrink(0)
        padding(4.px)
        border(0.px)
        backgroundColor(Color.transparent)
        color(SXMUI.playerForegroundColor.value())
        cursor("pointer")
        self + disabled style {
            opacity(.3)
            cursor("default")
        }
        self + hover style {
            color(SXMUI.playerMutedColor.value())
        }
        self + focusVisible style {
            property("outline", "2px solid ${SXMUI.playerAccentColor.value()}")
            property("outline-offset", "4px")
        }
    }

    val closeButton by style {
        paddingLeft(22.px)
        property("border-left", "1px solid ${SXMUI.playerDividerColor.value()}")
    }

    val seekArea by style {
        flex("1 1 auto")
        minWidth(80.px)
    }

    val timeDisplay by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(9.px)
        flexShrink(0)
        whiteSpace("nowrap")
        fontSize(.82.cssRem)
        fontWeight(600)
        property("font-variant-numeric", "tabular-nums")
    }

    val liveStatus by style {
        color(SXMUI.playerAccentColor.value())
        property("letter-spacing", ".07em")
    }

    val disabledStatus by style {
        color(SXMUI.playerTrackColor.value())
    }

    val liveDot by style {
        width(10.px)
        height(10.px)
        borderRadius(50.percent)
        property("background-color", "currentColor")
    }

    val volumeArea by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(16.px)
        flex("0 0 175px")
        paddingLeft(22.px)
        property("border-left", "1px solid ${SXMUI.playerDividerColor.value()}")
    }

    val spin by keyframes {
        from {
            property("transform", "rotate(0deg)")
        }
        to {
            property("transform", "rotate(360deg)")
        }
    }

    val spinner by style {
        width(22.px)
        height(22.px)
        borderRadius(50.percent)
        border(3.px, LineStyle.Solid, SXMUI.playerMutedColor.value())
        property("border-top-color", "${SXMUI.playerBackgroundColor.value()}")
        property("animation", "${spin.name} .8s linear infinite")
    }

    /** Expanded content rises from the transport on entry; metadata refreshes do not restart it. */
    val revealDetails by keyframes {
        from {
            property("transform", "translateY(24px)")
            opacity(0)
        }
        to {
            property("transform", "translateY(0)")
            opacity(1)
        }
    }

    val detailsPanel by style {
        position(Position.Relative)
        backgroundColor(SXMUI.playerDrawerBackgroundColor.value())
        padding(20.px, 70.px, 20.px, 20.px)
        color(SXMUI.playerForegroundColor.value())
        maxHeight(300.px)
        overflowY("auto")
        property("animation", "${revealDetails.name} 240ms cubic-bezier(.16, 1, .3, 1)")
    }

    val detailsColumns by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(0, 1.1fr) minmax(0, 1fr)")
        alignItems(AlignItems.Center)
        gap(32.px)
    }

    val cutIdentity by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(28.px)
        minWidth(0.px)
    }

    val cutArtwork by style {
        property("width", "clamp(112px, 12vw, 184px)")
        property("height", "clamp(112px, 12vw, 184px)")
        flexShrink(0)
        borderRadius(10.px)
        overflow("hidden")
        backgroundColor(SXMUI.playerBackgroundColor.value())
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }

    val detailEyebrow by style {
        color(SXMUI.playerDrawerMutedColor.value())
        fontSize(.84.cssRem)
        fontWeight(700)
        property("letter-spacing", ".1em")
        marginBottom(12.px)
    }

    val detailsTitle by style {
        property("font-size", "clamp(20px, 2.25vw, 34px)")
        fontWeight(800)
        property("line-height", "1.1")
        property("letter-spacing", "-.04em")
    }

    val detailsArtist by style {
        marginTop(5.px)
        property("font-size", "clamp(16px, 1.5vw, 24px)")
        property("line-height", "1.25")
    }

    val contextIdentity by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(24.px)
        paddingLeft(32.px)
        property("border-left", "1px solid ${SXMUI.playerDrawerDividerColor.value()}")
        minWidth(0.px)
    }

    val contextArtwork by style {
        width(128.px)
        height(128.px)
        borderRadius(12.px)
        overflow("hidden")
        flexShrink(0)
        backgroundColor(SXMUI.playerBackgroundColor.value())
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }

    val contextTitle by style {
        fontSize(1.85.cssRem)
        fontWeight(800)
        property("letter-spacing", "-.035em")
        property("line-height", "1.15")
    }

    val contextCaption by style {
        marginTop(10.px)
        fontSize(.86.cssRem)
        color(SXMUI.playerDrawerMutedColor.value())
        fontWeight(700)
        property("letter-spacing", ".12em")
        property("text-transform", "uppercase")
    }

    val relatedEntities by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        gap(6.px, 16.px)
        marginBottom(14.px)
        fontSize(1.4.cssRem)
    }

    val relatedSeparator by style {
        color(SXMUI.playerDrawerMutedColor.value())
    }

    val relatedLink by style {
        color(SXMUI.playerDrawerLinkColor.value())
        property("text-decoration", "underline")
        property("text-underline-offset", "3px")
        self + hover style {
            color(SXMUI.playerForegroundColor.value())
        }
    }

    val detailsNote by style {
        color(SXMUI.playerDrawerMutedColor.value())
        fontSize(.85.cssRem)
        marginTop(10.px)
    }

    val collapseButton by style {
        position(Position.Absolute)
        top(14.px)
        right(14.px)
        width(42.px)
        height(38.px)
        borderRadius(9.px)
        border(1.px, LineStyle.Solid, SXMUI.playerDrawerDividerColor.value())
        color(SXMUI.playerForegroundColor.value())
    }

    val warning by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(18.px)
        padding(12.px, 20.px)
        backgroundColor(SXMUI.messageWarnBackground.value())
        color(SXMUI.playerBackgroundColor.value())
        border(1.px, LineStyle.Solid, SXMUI.messageWarnBorder.value())
        fontSize(1.15.cssRem)
    }

    val warningIcon by style {
        color(SXMUI.playerWarningAccent.value())
        flexShrink(0)
    }

    val retryButton by style {
        property("margin-left", "auto")
        padding(8.px, 26.px)
        borderRadius(9.px)
        fontSize(1.cssRem)
        fontWeight(600)
        border(1.px, LineStyle.Solid, SXMUI.playerWarningAccent.value())
        backgroundColor(Color.transparent)
        color(SXMUI.playerWarningAccent.value())
        cursor("pointer")
    }
    /**
     * Clipping viewport for a metadata line that scrolls on hover. Font styling (size/weight/opacity)
     * comes from the [title] / [subTitle] class applied alongside this one; this class only guarantees
     * the line clips its overflow so the inner element can be scrolled behind a fixed window.
     */

    val marqueeViewport by style {
        overflow("hidden")
        position(Position.Relative)
    }
    /**
     * The moving inner element of a scroll-on-hover line. At rest it ellipsizes at the viewport width;
     * while its parent is armed and hovered it is sized to its full content and translated end-to-end.
     */

    val marqueeInner by style {
        display(DisplayStyle.InlineBlock)
        maxWidth(100.percent)
        whiteSpace("nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("will-change", "transform")
    }
    /** Left-to-right scroll of the inner text; the distance is supplied per-line as a CSS variable. */

    val marqueeScroll by keyframes {
        from {
            property("transform", "translateX(0)")
        }
        to {
            property("transform", "translateX(var(--marquee-end, 0px))")
        }
    }
    /**
     * Added to a line's viewport (by [MarqueeText]) only when its text overflows, so lines that fit
     * never move. On hover the inner element drops its ellipsis/width cap so the full text can travel
     * and scrolls end-to-end; the viewport (overflow: hidden) clips it. `alternate` scrolls out and
     * back; the per-line duration set as a CSS variable keeps the speed constant regardless of length.
     */

    val marqueeScrollable by style {
        desc(self + hover, className(marqueeInner)) style {
            property("max-width", "none")
            overflow("visible")
            property("text-overflow", "clip")
            // Reference the generated keyframes name (.name)
            // interpolating the delegate itself
            // serializes a broken CSSKeyframesRuleDeclaration(...) literal and the animation never runs.
            property("animation", "${marqueeScroll.name} var(--marquee-duration, 6s) linear infinite alternate")
        }
    }

    init {
        media(CSSMediaQuery.Raw("(prefers-reduced-motion: reduce)")) {
            className(detailsPanel) style {
                property("animation", "none")
            }
        }
        media(CSSMediaQuery.Raw("(max-width: 1100px)")) {
            className(primaryRow) style {
                gap(14.px)
                padding(12.px, 16.px)
            }
            className(sourceIdentity) style {
                flexBasis(230.px)
                minWidth(145.px)
                gap(12.px)
                paddingRight(14.px)
            }
            className(sourceWithDetails) style {
                flexBasis(300.px)
            }
            className(volumeArea) style {
                flexBasis(120.px)
                paddingLeft(14.px)
                gap(10.px)
            }
            className(contextArtwork) style {
                width(90.px)
                height(90.px)
            }
            className(contextTitle) style {
                fontSize(1.5.cssRem)
            }
            className(cutIdentity) style {
                gap(18.px)
            }
            className(contextIdentity) style {
                gap(18.px)
                paddingLeft(22.px)
            }
        }
        media(CSSMediaQuery.Raw("(max-width: 760px)")) {
            className(playerArea) style {
                margin(0.px)
            }
            className(primaryRow) style {
                flexWrap(FlexWrap.Wrap)
                gap(12.px)
            }
            className(sourceIdentity) style {
                flex("1 1 calc(100% - 120px)")
                property("border-right", "0")
                paddingRight(0.px)
            }
            className(previewImage) style {
                width(48.px)
                height(48.px)
            }
            className(transport) style {
                order(2)
                gap(12.px)
            }
            className(seekArea) style {
                order(3)
                flex("1 1 40px")
                minWidth(40.px)
            }
            className(timeDisplay) style {
                order(4)
            }
            className(volumeArea) style {
                order(5)
                flexBasis(80.px)
                gap(8.px)
                property("border-left", "0")
                paddingLeft(0.px)
            }
            className(closeButton) style {
                property("border-left", "0")
                paddingLeft(0.px)
            }
            className(detailsColumns) style {
                property("grid-template-columns", "minmax(0, 1fr)")
                gap(20.px)
            }
            className(detailsPanel) style {
                padding(18.px)
                maxHeight(380.px)
            }
            className(contextIdentity) style {
                property("border-left", "0")
                property("border-top", "1px solid ${SXMUI.playerDrawerDividerColor.value()}")
                paddingLeft(0.px)
                paddingTop(18.px)
            }
            className(cutIdentity) style {
                paddingRight(35.px)
            }
            className(cutArtwork) style {
                width(100.px)
                height(100.px)
            }
            className(contextArtwork) style {
                width(70.px)
                height(70.px)
            }
            className(relatedEntities) style {
                fontSize(.95.cssRem)
                marginBottom(8.px)
            }
            className(nowPlayingLabel) style {
                display(DisplayStyle.None)
            }
        }
    }
}
